<template>
  <div class="sticker-library">
    <div class="global-header-wrap">
      <div class="global-table-header">
        <div class="header-btn-left">
          <list-search ref="listSearch" @listSearchEvn="listSearchEvn" />
        </div>
        <div class="header-btn-right">
          <button class="global-button-style" @click="toUploadData">
            上传贴纸
          </button>
        </div>
      </div>
    </div>
    <div class="app-table-container" >
      <div class="global-body-wrap">
        <appTable :tableProps="tableProps" :tableData="formattedTableData" :selectCtrl="false"
          @tableHandle="tableHandle" />
      </div>
      <div style="display: flex; justify-content: flex-end; margin-top: 20px;">
        <pagination :currentPage="params.page" :pageSize="params.pageSize" :total="total" @pageNum="getPageNum" />
      </div>
    </div>

    <!-- 上传/编辑贴纸对话框 -->
    <el-dialog :title="detailId ? '编辑贴纸' : '上传贴纸'" :visible.sync="uploadVisible" width="90%" fullscreen
      :close-on-click-modal="false">
      <upload-data :detailId="detailId" @success="handleUploadSuccess" />
    </el-dialog>

    <!-- 操作详情对话框 -->
    <el-dialog title="操作详情" :visible.sync="optRecordVisible" width="90%" fullscreen>
      <opt-record :detailId="detailId" />
    </el-dialog>

    <!-- 测试对话框 -->
    <el-dialog title="测试" :visible.sync="testVisible" width="500px">
      <test @confirm="testConfirm" @close="testVisible = false" />
    </el-dialog>
  </div>
</template>

<script>
import listSearch from "@/components/listSearch";
import appTable from "@/components/appTable";
import pagination from "@/components/pagination";
import listMixin from "@/mixins/list";
import UploadData from "./components/uploadData/index.vue";
import OptRecord from "./components/optRecord/index.vue";
import Test from "@/components/Test.vue";
import api from "@/api";
import { valueByLanguage } from "@/utils/college";

const ShelfStatusEnum = {
  NOT_LISTED: 0,
  LISTED: 1,
  REMOVED: 2,
  PERMANENTLY_REMOVED: 3
};

const ShelfStatusMap = {
  0: "待上架",
  1: "已上架",
  2: "已下架",
  3: "已彻底下架"
};

const PaymentTypeMap = {
  FREE: "免费",
  PREMIUM: "付费",
  VIP_PRO: "Pro会员领取"
};

export default {
  name: "StickerLibrary",
  components: {
    listSearch,
    appTable,
    pagination,
    UploadData,
    OptRecord,
    Test
  },
  mixins: [listMixin],
  data() {
    return {
      params: {
        page: 1,
        pageSize: 20
      },
      tableData: [],
      total: 0,
      uploadVisible: false,
      optRecordVisible: false,
      testVisible: false,
      detailId: null
    };
  },
  computed: {
    tableProps() {
      return [
        {
          prop: "id",
          label: "贴纸id"
        },
        {
          prop: "thumbnail",
          label: "缩略图",
          width: 110
        },
        {
          prop: "thumbnailBadge",
          label: "缩略图角标"
        },
        {
          prop: "title",
          label: "标题"
        },
        {
          prop: "tags",
          label: "标签",
          minWidth: 200
        },
        {
          prop: "slogan",
          label: "宣传词"
        },
        {
          prop: "realUsageCount",
          label: "真实创建量"
        },
        {
          prop: "publisherName",
          label: "发布人"
        },
        {
          prop: "buttonBadge",
          label: "按钮角标"
        },
        {
          prop: "paymentType",
          label: "资料类型"
        },
        {
          prop: "price",
          label: "原价"
        },
        {
          prop: "price1",
          label: "常规折扣价"
        },
        {
          prop: "price2",
          label: "文星会员折扣价"
        },
        {
          prop: "price3",
          label: "Pro会员折扣价"
        },
        {
          prop: "status",
          label: "状态",
          width: 100
        },
        {
          prop: "handle",
          label: "操作",
          width: 500,
          btn: ["编辑", "状态", "测试", "复制链接", "操作详情"]
        }
      ];
    }
    ,
    formattedTableData() {
      return this.tableData.map((row) => {
        // 多语言字段统一转成简体中文文案，供表格直接展示
        const thumbnail = valueByLanguage(row.thumbnail, "zh_CN", true);
        const thumbnailBadge = valueByLanguage(row.thumbnailBadge, "zh_CN", true);
        const title = valueByLanguage(row.title, "zh_CN", true);
        const slogan = valueByLanguage(row.slogan, "zh_CN", true);
        const buttonBadge = valueByLanguage(row.buttonBadge, "zh_CN", true);
        const publisherName = row.publisher
          ? valueByLanguage(row.publisher.name, "zh_CN", true) || row.publisher.name || "-"
          : "-";

        // 标签列：appTable 对 prop === 'tags' 有专门渲染逻辑，期望是数组，
        // 其中每一项要么是字符串，要么是 { id, name }，这里把多语言 name 收敛成简体中文字符串
        const tags = Array.isArray(row.tags)
          ? row.tags
            .map((tag) => {
              if (!tag) return null;
              const rawName = (() => {
                if (!tag.name) return "";
                // 多语言结构 [{ locale, value }, ...]
                if (Array.isArray(tag.name)) {
                  return valueByLanguage(tag.name, "zh_CN", true) || "";
                }
                // 已经是字符串
                return String(tag.name || "");
              })();
              if (!rawName || !rawName.trim()) return null;
              // 返回带有简体中文 name 的对象，兼容 appTable 对 t.id / t.name 的使用
              return {
                ...tag,
                name: rawName
              };
            })
            .filter((t) => t !== null)
          : [];

        const statusMap = ShelfStatusMap;
        const statusBtnMap = {
          0: "上架",
          1: "下架",
          2: "彻底下架",
          3: "已彻底下架"
        };

        const paymentInfo = row.paymentInfo || {};
        const paymentType = paymentInfo.type;

        return {
          ...row,
          thumbnail: thumbnail || "",
          thumbnailBadge: thumbnailBadge || "-",
          title: title || "-",
          tags,
          slogan: slogan || "-",
          publisherName,
          buttonBadge: buttonBadge || "-",
          paymentType: PaymentTypeMap[paymentType] || "-",
          price:
            paymentInfo.price !== null && paymentInfo.price !== undefined
              ? (paymentInfo.price / 100).toFixed(2)
              : "-",
          price1:
            paymentInfo.price1 !== null && paymentInfo.price1 !== undefined
              ? (paymentInfo.price1 / 100).toFixed(2)
              : "-",
          price2:
            paymentInfo.price2 !== null && paymentInfo.price2 !== undefined
              ? (paymentInfo.price2 / 100).toFixed(2)
              : "-",
          price3:
            paymentInfo.price3 !== null && paymentInfo.price3 !== undefined
              ? (paymentInfo.price3 / 100).toFixed(2)
              : "-",
          status: statusMap[row.status] || "未知",
          statusBtnText: statusBtnMap[row.status] || "状态"
        };
      });
    }
  },
  mounted() {
    this.getList();
  },
  methods: {
    getList() {
      api
        .getStickerList(this.params)
        .then((res) => {
          this.tableData = res.data.items || [];
          this.total = res.data.count || 0;
        })
        .catch((err) => {
          this.$message.error(err.message || "获取列表失败");
        });
    },
    listSearchEvn(values) {
      this.params = {
        page: 1,
        pageSize: 20,
        text: values
      };
      this.getList();
    },
    getPageNum(page) {
      this.params.page = page;
      this.getList();
    },
    tableHandle(data) {
      const row = data.data && data.data.row ? data.data.row : data.row;

      if (!row || !row.id) return;
      const originalItem = this.tableData.find((item) => item && item.id === row.id);
      if (!originalItem) return;

      if (data.type === "编辑") {
        this.toUploadData(originalItem);
      } else if (data.type === "状态") {
        this.updateShelfStatus(originalItem);
      } else if (data.type === "测试") {
        this.openTest(originalItem);
      } else if (data.type === "复制链接") {
        this.copyLink(originalItem);
      } else if (data.type === "操作详情") {
        this.openOptRecord(originalItem);
      }
    },
    toUploadData(row) {
      this.detailId = row ? row.id : null;
      this.uploadVisible = true;
    },
    updateShelfStatus(row) {
      if (row.status === ShelfStatusEnum.PERMANENTLY_REMOVED) {
        this.$message.warning("已彻底下架，无法操作");
        return;
      }
      const newStatus = row.status + 1;
      api
        .updateStickerStatus(row.id, { status: newStatus })
        .then(() => {
          this.$message.success("操作成功");
          this.getList();
        })
        .catch((err) => {
          this.$message.error(err.message || "操作失败");
        });
    },
    openOptRecord(row) {
      this.detailId = row.id;
      this.optRecordVisible = true;
    },
    copyLink(row) {
      try {
        const url = `${window.location.origin}/library/material/detail?id=${row.uniqueId}&lang=zh&type=STICKER`;
        const textarea = document.createElement("textarea");
        textarea.value = url;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.$message.success("复制成功，请在浏览器粘贴");
      } catch (err) {
        this.$message.error("复制失败");
      }
    },
    openTest(row) {
      this.detailId = row.id;
      this.testVisible = true;
    },
    testConfirm(data) {
      api
        .testSticker(this.detailId, data)
        .then(() => {
          this.$message.success("测试成功");
          this.testVisible = false;
        })
        .catch((err) => {
          this.$message.error(err.message || "测试失败");
        });
    },
    handleUploadSuccess() {
      this.uploadVisible = false;
      this.detailId = null;
      this.getList();
    }
  }
};
</script>

<style scoped>
.global-header-wrap {
  padding:0 20px;
}
.app-table-container{
  padding:0 20px;
}
</style>
